import React, { useEffect, useState } from 'react';
import { dlpService } from '../../services/dlp.service';
import { SteelReportTable } from './SteelReportTable';
import { SteelDeptTop5MonthCompare } from './SteelDeptTop5MonthCompare';
import { computePreviousPeriod } from '../../lib/dlpSteelTable';

interface Props {
  period: string;
  company: string;
}

/** API 표 이름 → 화면 표시 제목 (요구사항 문서·Confluence 첨부 스크린샷 기준) */
const DEPT_TOP5_TABLE_NAME = '부서 출력 TOP5';
const DEPT_TOP5_DISPLAY_TITLE = '워터마크 해제 신청 부서 TOP5';

/**
 * IPT-623 — 표별 컬럼 순서 강제 (운영 캐시 JSONB 왕복 시 원본 API 키 순서가
 * 보존되지 않아, 명시적으로 순서를 지정해야 하는 표만 등록한다).
 */
const COLUMN_ORDER: Record<string, string[]> = {
  '워터마크 해제 신청 건수': ['구분', '건수'],
};

/**
 * 세아제강 계열 DLP — 출력 탭 (IPT-441, Privacy-i API report_type=print).
 * 2026-09-15 후속 확인 — "워터마크 해제 신청 부서 TOP5"는 이번달만이 아니라
 * 전월 데이터도 나란히 보여줘야 한다(요구사항 문서 "지난달 데이터가 확인되어야 함").
 * 이번달/전월 각각 캐시 조회이므로 API를 두 번 호출한다.
 */
export const SteelOutputTab: React.FC<Props> = ({ period, company }) => {
  const [tables, setTables] = useState<Record<string, Record<string, string>[]>>({});
  const [previousDeptRows, setPreviousDeptRows] = useState<Record<string, string>[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const previousPeriod = period ? computePreviousPeriod(period) : '';

  useEffect(() => {
    if (!period || !company) return;
    setIsLoading(true);
    Promise.all([
      dlpService.getSteelReport(period, company),
      dlpService.getSteelReport(previousPeriod, company),
    ])
      .then(([current, previous]) => {
        setTables(current.reports.print);
        setPreviousDeptRows(previous.reports.print[DEPT_TOP5_TABLE_NAME] ?? []);
      })
      .catch(() => {
        setTables({});
        setPreviousDeptRows([]);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, company]);

  if (isLoading) return <div>로딩 중...</div>;

  return (
    <div className="tab-panel steel-output-tab">
      <h2>출력</h2>
      {Object.entries(tables).map(([tableName, rows]) => {
        if (tableName === DEPT_TOP5_TABLE_NAME) {
          return (
            <SteelDeptTop5MonthCompare
              key={tableName}
              title={DEPT_TOP5_DISPLAY_TITLE}
              currentPeriod={period}
              previousPeriod={previousPeriod}
              currentRows={rows}
              previousRows={previousDeptRows}
            />
          );
        }
        return (
          <SteelReportTable
            key={tableName}
            title={tableName}
            rows={rows}
            columnOrder={COLUMN_ORDER[tableName]}
          />
        );
      })}
      {Object.keys(tables).length === 0 && <div>데이터가 없습니다.</div>}
    </div>
  );
};
